/**
 * WatchButton — one-click "Watch" pill for any ticker surfacing.
 *
 * Wraps /api/watchlist POST with optimistic state transitions:
 *   idle → loading → added (or → already if duplicate)
 *
 * Clean badge styling so it sits in any tight row without disturbing
 * layout. Prevents click-bubble so it can live inside parent <button>
 * / <a> wrappers without triggering their handlers.
 */
import { useState } from 'react'
import { BookmarkPlus, BookmarkCheck } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'

interface Props {
  ticker: string
  note?: string      // recorded in the watchlist item 'notes' field
  size?: 'xs' | 'sm'
  className?: string
}

export default function WatchButton({ ticker, note = 'Added from signal surface', size = 'xs', className }: Props) {
  const [state, setState] = useState<'idle' | 'loading' | 'added' | 'already'>('idle')

  const add = async (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (state === 'loading' || state === 'added' || state === 'already') return
    setState('loading')
    try {
      await api.post('/api/watchlist', {
        ticker,
        name: ticker,
        notes: note,
        alerts: [],
      })
      setState('added')
    } catch (err: any) {
      const detail = err?.response?.data?.detail || ''
      if (detail.toString().toLowerCase().includes('already')) {
        setState('already')
      } else {
        setState('idle')
      }
    }
  }

  const isOn = state === 'added' || state === 'already'
  const sizeClass = size === 'xs'
    ? 'px-1.5 py-0.5 text-[10px] gap-1'
    : 'px-2 py-1 text-xs gap-1'
  const iconSize = size === 'xs' ? 10 : 12

  return (
    <button
      type="button"
      onClick={add}
      disabled={state === 'loading' || isOn}
      title={
        state === 'already' ? 'Already on watchlist' :
        state === 'added' ? 'Added to watchlist' :
        `Add ${ticker} to watchlist`
      }
      className={clsx(
        'inline-flex items-center rounded border transition',
        sizeClass,
        isOn
          ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
          : 'bg-surface border-border text-text-muted hover:text-text-primary hover:border-white/30',
        className,
      )}
    >
      {isOn ? <BookmarkCheck size={iconSize} /> : <BookmarkPlus size={iconSize} />}
      <span>{state === 'loading' ? '…' : isOn ? 'Watching' : 'Watch'}</span>
    </button>
  )
}
